import axios from "axios";
import { getBackendBaseUrl } from "@/lib/config";

const API_URL = getBackendBaseUrl();
const PRICING_CACHE_KEY = "voxara_pricing_snapshot_v1";
const PRICING_CACHE_TTL_MS = 5 * 60 * 1000;

let pricingSnapshotMemory = null;
let pricingPrefetchPromise = null;

const isFreshSnapshot = (snapshot) => {
  if (!snapshot?.fetchedAt) return false;
  return Date.now() - Number(snapshot.fetchedAt) < PRICING_CACHE_TTL_MS;
};

const normalizeViewerKey = (viewerKey) => String(viewerKey || "guest");

const isSnapshotOwnedByViewer = (snapshot, viewerKey) => {
  if (!snapshot) return false;
  return normalizeViewerKey(snapshot.viewerKey) === normalizeViewerKey(viewerKey);
};

export const getCachedPricingSnapshot = (viewerKey = "guest") => {
  if (isFreshSnapshot(pricingSnapshotMemory) && isSnapshotOwnedByViewer(pricingSnapshotMemory, viewerKey)) {
    return pricingSnapshotMemory;
  }

  try {
    const raw = sessionStorage.getItem(PRICING_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isFreshSnapshot(parsed) || !isSnapshotOwnedByViewer(parsed, viewerKey)) {
      sessionStorage.removeItem(PRICING_CACHE_KEY);
      return null;
    }
    pricingSnapshotMemory = parsed;
    return parsed;
  } catch {
    return null;
  }
};

export const storePricingSnapshot = (snapshot, viewerKey = "guest") => {
  if (!snapshot) return null;
  const normalizedSnapshot = {
    ...snapshot,
    viewerKey: normalizeViewerKey(viewerKey),
  };
  pricingSnapshotMemory = normalizedSnapshot;
  try {
    sessionStorage.setItem(PRICING_CACHE_KEY, JSON.stringify(normalizedSnapshot));
  } catch {
    // noop
  }
  return normalizedSnapshot;
};

export const clearPricingSnapshot = () => {
  pricingSnapshotMemory = null;
  pricingPrefetchPromise = null;
  try {
    sessionStorage.removeItem(PRICING_CACHE_KEY);
  } catch {
    // noop
  }
};

export const prefetchPricingSnapshot = async ({ includeSubscription = false, force = false, viewerKey = "guest" } = {}) => {
  const cached = getCachedPricingSnapshot(viewerKey);
  if (cached && (!includeSubscription || cached.subscription)) {
    return cached;
  }

  if (pricingPrefetchPromise && !force) {
    return pricingPrefetchPromise;
  }

  pricingPrefetchPromise = (async () => {
    const requests = [
      axios.get(`${API_URL}/api/subscription/plans`),
      axios.get(`${API_URL}/api/subscription/providers`),
    ];

    if (includeSubscription) {
      requests.push(axios.get(`${API_URL}/api/subscription/status`));
    }

    const [plansResponse, providersResponse, subscriptionResponse] = await Promise.all(requests);
    return storePricingSnapshot({
      plans: plansResponse.data.plans || [],
      providers: providersResponse.data || null,
      subscription: subscriptionResponse?.data || null,
      fetchedAt: Date.now(),
    }, viewerKey);
  })();

  try {
    return await pricingPrefetchPromise;
  } finally {
    pricingPrefetchPromise = null;
  }
};